import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Alert,
} from 'react-native';
import { Icon, type IconName } from './Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import { noteRepository, type CustomerNoteRecord, type NoteType } from '../repositories';

export interface QuickNoteModalProps {
  visible: boolean;
  customerId: string;
  customerName: string;
  onClose: () => void;
  onSaved?: () => void;
}

export const QuickNoteModal: React.FC<QuickNoteModalProps> = ({
  visible,
  customerId,
  customerName,
  onClose,
  onSaved,
}) => {
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState<NoteType>('journal');
  const [existingNotes, setExistingNotes] = useState<CustomerNoteRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && customerId) {
      loadNotes();
    }
  }, [visible, customerId]);

  const loadNotes = async () => {
    try {
      const notes = await noteRepository.getByCustomerId(customerId);
      setExistingNotes(notes);
    } catch (e) {
      console.error('Lỗi tải ghi chú:', e);
    }
  };

  if (!visible) return null;

  const handleSave = async () => {
    if (!noteContent.trim()) {
      Alert.alert('Thông báo', 'Vui lòng nhập nội dung ghi chú');
      return;
    }
    setLoading(true);
    try {
      const today = new Date().toLocaleDateString('vi-VN');
      await noteRepository.save({
        customerId,
        type: noteType,
        content: noteContent.trim(),
        author: 'Nguyễn Văn Cao (FA)',
        date: today,
      });
      setNoteContent('');
      await loadNotes();
      Alert.alert('Thành công', 'Đã thêm ghi chú cho khách hàng!');
      if (onSaved) onSaved();
    } catch (e) {
      Alert.alert('Lỗi', 'Không thể lưu ghi chú.');
    } finally {
      setLoading(false);
    }
  };

  const typeLabels: Record<NoteType, { label: string; icon: IconName; color: string; bg: string }> = {
    journal: { label: 'Nhật ký tư vấn', icon: 'description', color: '#003366', bg: '#EFF6FF' },
    gift: { label: 'Tặng quà chăm sóc', icon: 'star', color: '#D97706', bg: '#FFFBEB' },
    claims: { label: 'Hỗ trợ rủi ro/BH', icon: 'medical_services', color: '#E11D48', bg: '#FFF1F2' },
  };

  return (
    <View style={styles.backdrop}>
      <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={onClose} />
      <View style={styles.card}>
        <View style={styles.header}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing[2] }}>
            <View style={styles.iconCircle}>
              <Icon name="comment" size={20} color={colors.primary} />
            </View>
            <View>
              <Text style={styles.title}>Ghi chú khách hàng</Text>
              <Text style={styles.subtitle}>{customerName}</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
            <Icon name="close" size={20} color={colors.onSurfaceVariant} />
          </TouchableOpacity>
        </View>

        <View style={styles.body}>
          {/* Note Type Selector */}
          <Text style={styles.inputLabel}>Loại ghi chú</Text>
          <View style={styles.typeRow}>
            {(['journal', 'gift', 'claims'] as NoteType[]).map((t) => {
              const meta = typeLabels[t];
              const isSelected = noteType === t;
              return (
                <TouchableOpacity
                  key={t}
                  style={[
                    styles.typeChip,
                    { backgroundColor: isSelected ? meta.bg : '#F3F4F6', borderColor: isSelected ? meta.color : '#E5E7EB' },
                  ]}
                  onPress={() => setNoteType(t)}
                >
                  <Icon name={meta.icon} size={14} color={isSelected ? meta.color : colors.onSurfaceVariant} />
                  <Text style={[styles.typeChipText, { color: isSelected ? meta.color : colors.onSurfaceVariant }]}>
                    {meta.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Text Input */}
          <Text style={styles.inputLabel}>Nội dung ghi chú</Text>
          <TextInput
            style={styles.textInput}
            placeholder="Nhập ghi chú tư vấn, sinh nhật, nhu cầu sản phẩm, v.v..."
            multiline
            numberOfLines={3}
            value={noteContent}
            onChangeText={setNoteContent}
          />

          <TouchableOpacity
            style={[styles.saveBtn, loading && styles.disabledBtn]}
            onPress={handleSave}
            disabled={loading}
          >
            <Icon name="add" size={16} color="#ffffff" style={{ marginRight: 4 }} />
            <Text style={styles.saveBtnText}>{loading ? 'Đang lưu...' : 'Thêm ghi chú'}</Text>
          </TouchableOpacity>

          {/* Recent Notes */}
          <Text style={styles.sectionHeader}>Ghi chú đã lưu ({existingNotes.length})</Text>
          <ScrollView style={styles.notesList} nestedScrollEnabled>
            {existingNotes.length === 0 ? (
              <Text style={styles.emptyText}>Chưa có ghi chú nào cho khách hàng này.</Text>
            ) : (
              existingNotes.map((n) => {
                const meta = typeLabels[n.type] || typeLabels.journal;
                return (
                  <View key={n.id} style={styles.noteItem}>
                    <View style={styles.noteMetaRow}>
                      <View style={[styles.noteBadge, { backgroundColor: meta.bg }]}>
                        <Text style={[styles.noteBadgeText, { color: meta.color }]}>{meta.label}</Text>
                      </View>
                      <Text style={styles.noteDate}>{n.date}</Text>
                    </View>
                    <Text style={styles.noteContentText}>{n.content}</Text>
                    <Text style={styles.noteAuthor}>Bởi: {n.author}</Text>
                  </View>
                );
              })
            )}
          </ScrollView>
        </View>

        <View style={styles.footer}>
          <TouchableOpacity style={styles.closeFooterBtn} onPress={onClose}>
            <Text style={styles.closeFooterBtnText}>Đóng</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    zIndex: 2000,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    width: '100%',
    maxWidth: 500,
    maxHeight: '85%',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
    padding: spacing[5],
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    paddingBottom: spacing[3],
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F0FDF4',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  subtitle: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  closeBtn: {
    padding: spacing[1],
  },
  body: {
    flexShrink: 1,
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[1.5],
  },
  typeRow: {
    flexDirection: 'row',
    gap: spacing[2],
    marginBottom: spacing[3],
    flexWrap: 'wrap',
  },
  typeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1.5],
    borderRadius: borderRadius.full,
    borderWidth: 1,
  },
  typeChipText: {
    fontSize: 11,
    fontWeight: fontWeights.medium,
  },
  textInput: {
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[3],
    fontSize: 13,
    color: colors.onSurface,
    backgroundColor: '#F9FAFB',
    textAlignVertical: 'top',
    minHeight: 70,
    marginBottom: spacing[3],
  },
  saveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.DEFAULT,
    marginBottom: spacing[4],
  },
  saveBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: fontWeights.bold,
  },
  disabledBtn: {
    opacity: 0.6,
  },
  sectionHeader: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  notesList: {
    maxHeight: 180,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: spacing[2],
  },
  emptyText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    fontStyle: 'italic',
    textAlign: 'center',
    marginVertical: spacing[2],
  },
  noteItem: {
    backgroundColor: '#F9FAFB',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[2.5],
    marginBottom: spacing[2],
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  noteMetaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  noteBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  noteBadgeText: {
    fontSize: 10,
    fontWeight: fontWeights.semibold,
  },
  noteDate: {
    fontSize: 10,
    color: colors.onSurfaceVariant,
  },
  noteContentText: {
    fontSize: 12,
    color: colors.onSurface,
    marginBottom: 4,
  },
  noteAuthor: {
    fontSize: 10,
    color: colors.onSurfaceVariant,
    fontStyle: 'italic',
  },
  footer: {
    marginTop: spacing[3],
    alignItems: 'flex-end',
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    paddingTop: spacing[2],
  },
  closeFooterBtn: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2],
    borderRadius: borderRadius.DEFAULT,
    backgroundColor: '#F3F4F6',
  },
  closeFooterBtnText: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
});
